import { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Button,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import WarningIcon from "@mui/icons-material/Warning";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import adminAPI, { AdminUser, AdminCreditLedger, CreditBalances } from "../admin.api";

interface UserCreditsTabProps {
  user: AdminUser;
}

function UserCreditsTab({ user }: UserCreditsTabProps) {
  const [credits, setCredits] = useState<AdminCreditLedger[]>([]);
  const [balances, setBalances] = useState<CreditBalances | null>(null);
  const [totalEntries, setTotalEntries] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<"view" | "add" | "remove">("view");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLedgerEntry, setSelectedLedgerEntry] = useState<AdminCreditLedger | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<AdminCreditLedger | null>(null);
  const [editAmount, setEditAmount] = useState<number>(0);
  const [editPaddleSubscriptionId, setEditPaddleSubscriptionId] = useState<string>("");
  const [removeAllDialogOpen, setRemoveAllDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [removingAll, setRemovingAll] = useState(false);
  const [removeExpiredDialogOpen, setRemoveExpiredDialogOpen] = useState(false);
  const [removingExpired, setRemovingExpired] = useState(false);

  // Add credit form
  const [addCreditForm, setAddCreditForm] = useState({
    creditType: "ARTICLE",
    amount: 0,
    description: "",
    expiresAt: "",
  });

  // Remove credit form
  const [removeCreditForm, setRemoveCreditForm] = useState({
    creditType: "ARTICLE",
    amount: 0,
    description: "",
  });

  const creditTypes = [
    "ARTICLE",
    "NICHE_TOOL",
    "HEADLINE_TOOL",
    "KEYWORD_TOOL",
  ];

  const fetchCredits = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminAPI.getUserCredits(user._id);
      setCredits(response.credits);
      setBalances(response.balances);
      setTotalEntries(response.pagination.total);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || "Failed to fetch credits");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCredits();
  }, [user._id]);

  const handleAddCredits = async () => {
    setError(null);
    try {
      await adminAPI.addCredits(
        user._id,
        addCreditForm.creditType,
        addCreditForm.amount,
        addCreditForm.description,
        addCreditForm.expiresAt || undefined
      );
      fetchCredits();
      setAddCreditForm({
        creditType: "ARTICLE",
        amount: 0,
        description: "",
        expiresAt: "",
      });
      setMode("view");
    } catch (err: any) {
      setError(err.response?.data?.error?.message || "Failed to add credits");
    }
  };

  const handleRemoveCredits = async () => {
    setError(null);
    try {
      await adminAPI.removeCredits(
        user._id,
        removeCreditForm.creditType,
        removeCreditForm.amount,
        removeCreditForm.description
      );
      fetchCredits();
      setRemoveCreditForm({
        creditType: "ARTICLE",
        amount: 0,
        description: "",
      });
      setMode("view");
    } catch (err: any) {
      setError(err.response?.data?.error?.message || "Failed to remove credits");
    }
  };

  const handleDeleteClick = (entry: AdminCreditLedger) => {
    setSelectedLedgerEntry(entry);
    setDeleteDialogOpen(true);
  };

  const handleEditClick = (entry: AdminCreditLedger) => {
    setEditingEntry(entry);
    setEditAmount(entry.value);
    setEditPaddleSubscriptionId(entry.paddleSubscriptionId || entry.subscription?.paddleSubscriptionId || "");
    setEditDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedLedgerEntry) return;
    
    setError(null);
    try {
      await adminAPI.deleteCreditLedgerEntry(selectedLedgerEntry._id);
      fetchCredits();
      setDeleteDialogOpen(false);
      setSelectedLedgerEntry(null);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || "Failed to delete ledger entry");
      setDeleteDialogOpen(false);
    }
  };

  const handleRemoveAllCredits = async () => {
    if (confirmText !== "REMOVE ALL") return;
    
    setRemovingAll(true);
    setError(null);
    try {
      await adminAPI.deleteAllCreditLedgerEntries(user._id);
      await fetchCredits();

      setRemoveAllDialogOpen(false);
      setConfirmText("");
    } catch (err: any) {
      setError(err.response?.data?.error?.message || "Failed to remove all credits");
    } finally {
      setRemovingAll(false);
    }
  };

  const handleRemoveExpiredCredits = async () => {
    setRemovingExpired(true);
    setError(null);
    try {
      // Filter and delete ALL expired entries (both credit and debit)
      const expiredEntries = credits.filter(credit => {
        if (!credit.expiresAt) return false;
        return new Date(credit.expiresAt) < new Date();
      });
      
      if (expiredEntries.length > 0) {
        // Delete each expired entry
        const promises = expiredEntries.map(entry => 
          adminAPI.deleteCreditLedgerEntry(entry._id)
        );
        
        await Promise.all(promises);
        fetchCredits();
      }
      
      setRemoveExpiredDialogOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || "Failed to remove expired credits");
    } finally {
      setRemovingExpired(false);
    }
  };

  // Count expired entries (both credit and debit)
  const expiredCreditsCount = credits.filter(credit => {
    if (!credit.expiresAt) return false;
    return new Date(credit.expiresAt) < new Date();
  }).length;


  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Info Box about Credits */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
          How Credits Work
        </Typography>
        <Typography variant="body2" component="div">
          • <strong>Credit Types:</strong> ARTICLE (for article generation), NICHE_TOOL, HEADLINE_TOOL, KEYWORD_TOOL (for AI tools)
        </Typography>
        <Typography variant="body2" component="div">
          • <strong>Expiration:</strong> Credits can have expiration dates - expired credits are automatically skipped during usage
        </Typography>
        <Typography variant="body2" component="div">
          • <strong>Usage Order:</strong> Credits are consumed oldest first (FIFO), with expiring credits prioritized
        </Typography>
        <Typography variant="body2" component="div">
          • <strong>Balance:</strong> Shows total available credits after accounting for debits and expired credits
        </Typography>
      </Alert>

      <Box sx={{ mb: 3, display: "flex", gap: 1.5, alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant={mode === "add" ? "contained" : "outlined"}
            onClick={() => setMode("add")}
            color="success"
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: mode === "add" ? 600 : 500,
              px: 2,
              ...(mode !== "add" && {
                border: '1px solid',
                '&:hover': {
                  border: '1px solid',
                }
              })
            }}
          >
            Add Credits
          </Button>
          <Button
            variant={mode === "remove" ? "contained" : "outlined"}
            onClick={() => setMode("remove")}
            color="error"
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: mode === "remove" ? 600 : 500,
              px: 2,
              ...(mode !== "remove" && {
                border: '1px solid',
                '&:hover': {
                  border: '1px solid',
                }
              })
            }}
          >
            Deduct Credits
          </Button>
        </Box>
        
        {/* Nuclear Options */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {expiredCreditsCount > 0 && (
            <Button
              variant="outlined"
              onClick={() => setRemoveExpiredDialogOpen(true)}
              color="warning"
              startIcon={<DeleteIcon />}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                border: '1px solid',
                borderColor: 'warning.main',
                '&:hover': {
                  bgcolor: 'warning.lighter',
                  borderColor: 'warning.dark',
                  border: '1px solid',
                }
              }}
            >
              Remove Expired ({expiredCreditsCount})
            </Button>
          )}
          
          {totalEntries > 0 && (
            <Button
              variant="outlined"
              onClick={() => setRemoveAllDialogOpen(true)}
              color="error"
              startIcon={<DeleteForeverIcon />}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                border: '1px solid',
                borderColor: 'error.main',
                '&:hover': {
                  bgcolor: 'error.lighter',
                  borderColor: 'error.dark',
                  border: '1px solid',
                }
              }}
            >
              Delete All Ledger Entries
            </Button>
          )}
        </Box>
      </Box>

      {mode === "add" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
          <TextField
            select
            label="Credit Type"
            value={addCreditForm.creditType}
            onChange={(e) =>
              setAddCreditForm((prev) => ({ ...prev, creditType: e.target.value }))
            }
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          >
            {creditTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type.replace(/_/g, " ")}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Amount"
            type="number"
            value={addCreditForm.amount}
            onChange={(e) =>
              setAddCreditForm((prev) => ({
                ...prev,
                amount: parseInt(e.target.value) || 0,
              }))
            }
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          />

          <TextField
            label="Description"
            value={addCreditForm.description}
            onChange={(e) =>
              setAddCreditForm((prev) => ({ ...prev, description: e.target.value }))
            }
            fullWidth
            multiline
            rows={2}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          />

          <TextField
            label="Expires At (Optional)"
            type="date"
            value={addCreditForm.expiresAt}
            onChange={(e) =>
              setAddCreditForm((prev) => ({ ...prev, expiresAt: e.target.value }))
            }
            fullWidth
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          />

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              color="success"
              onClick={handleAddCredits}
              disabled={!addCreditForm.amount || !addCreditForm.description}
            >
              Add Credits
            </Button>
            <Button variant="outlined" onClick={() => setMode("view")}>
              Cancel
            </Button>
          </Box>
        </Box>
      )}

      {mode === "remove" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
          <TextField
            select
            label="Credit Type"
            value={removeCreditForm.creditType}
            onChange={(e) =>
              setRemoveCreditForm((prev) => ({ ...prev, creditType: e.target.value }))
            }
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          >
            {creditTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type.replace(/_/g, " ")}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Amount to Deduct"
            type="number"
            value={removeCreditForm.amount}
            onChange={(e) =>
              setRemoveCreditForm((prev) => ({
                ...prev,
                amount: parseInt(e.target.value) || 0,
              }))
            }
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          />

          <TextField
            label="Reason for Deduction"
            value={removeCreditForm.description}
            onChange={(e) =>
              setRemoveCreditForm((prev) => ({ ...prev, description: e.target.value }))
            }
            fullWidth
            multiline
            rows={2}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          />

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              color="error"
              onClick={handleRemoveCredits}
              disabled={!removeCreditForm.amount || !removeCreditForm.description}
            >
              Deduct Credits
            </Button>
            <Button variant="outlined" onClick={() => setMode("view")}>
              Cancel
            </Button>
          </Box>
        </Box>
      )}

      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Credit History
      </Typography>
      
      <TableContainer 
        component={Paper}
        elevation={0}
        sx={{ 
          borderRadius: 3,
          overflow: 'hidden',
          '& .MuiTable-root': {
            borderCollapse: 'separate',
            borderSpacing: 0,
          }
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ 
              bgcolor: 'background.paper',
              '& .MuiTableCell-head': {
                fontWeight: 600,
                fontSize: '0.875rem',
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                borderBottom: '2px solid',
                borderColor: 'divider',
                py: 2,
              }
            }}>
              <TableCell>Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Credit Type</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Paddle ID</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {credits.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                  <Typography color="text.secondary" variant="body2">
                    No credit history available
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              credits.map((credit, index) => (
                <TableRow 
                  key={credit._id}
                  sx={{
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                    '& .MuiTableCell-body': {
                      borderBottom: index === credits.length - 1 ? 'none' : '1px solid',
                      borderColor: 'divider',
                      py: 2,
                      fontSize: '0.875rem',
                    },
                    transition: 'background-color 0.2s',
                  }}
                >
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {new Date(credit.createdAt).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(credit.createdAt).toLocaleTimeString()}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={credit.type === "credit" ? "Added" : "Used"}
                      color={credit.type === "credit" ? "success" : "default"}
                      size="small"
                      sx={{ 
                        fontWeight: 600,
                        borderRadius: 1.5,
                        textTransform: 'capitalize'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={credit.creditType.replace(/_/g, " ")}
                      size="small"
                      variant="outlined"
                      sx={{ 
                        borderRadius: 1.5,
                        textTransform: 'capitalize',
                        fontSize: '0.75rem'
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color={credit.type === "credit" ? "success.main" : "text.primary"}
                    >
                      {credit.type === "credit" ? "+" : "-"}
                      {Math.abs(credit.value)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {(credit.paddleSubscriptionId || credit.subscription?.paddleSubscriptionId) ? (
                      <code style={{ fontSize: "0.75rem" }}>
                        {credit.paddleSubscriptionId || credit.subscription?.paddleSubscriptionId}
                      </code>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 300 }}>
                      {credit.description || credit.title || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {credit.expiresAt ? (
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {new Date(credit.expiresAt).toLocaleDateString()}
                        </Typography>
                        {new Date(credit.expiresAt) < new Date() && (
                          <Chip 
                            label="Expired" 
                            size="small" 
                            color="error" 
                            sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Never
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditClick(credit)}
                        title="Edit Amount"
                        sx={{
                          color: 'primary.main',
                          '&:hover': {
                            bgcolor: 'primary.lighter',
                          },
                          transition: 'all 0.2s',
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(credit)}
                        title="Delete Entry"
                        sx={{
                          color: 'text.secondary',
                          '&:hover': {
                            color: 'error.main',
                            bgcolor: 'error.lighter',
                          },
                          transition: 'all 0.2s',
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Credit Ledger Entry</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this credit ledger entry? This action cannot be undone.
            {selectedLedgerEntry && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Type:</strong> {selectedLedgerEntry.type}
                </Typography>
                <Typography variant="body2">
                  <strong>Credit Type:</strong> {selectedLedgerEntry.creditType}
                </Typography>
                <Typography variant="body2">
                  <strong>Amount:</strong> {selectedLedgerEntry.value}
                </Typography>
                <Typography variant="body2">
                  <strong>Description:</strong> {selectedLedgerEntry.description || selectedLedgerEntry.title || "N/A"}
                </Typography>
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Credit Entry Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Credit Entry</DialogTitle>
        <DialogContent>
          {editingEntry && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Type:</strong> {editingEntry.type === "credit" ? "Credit (Added)" : "Debit (Used)"}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Credit Type:</strong> {editingEntry.creditType.replace(/_/g, " ")}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Description:</strong> {editingEntry.description || editingEntry.title || "N/A"}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Created:</strong> {new Date(editingEntry.createdAt).toLocaleString()}
              </Typography>

              <TextField
                label="Amount"
                type="number"
                value={editAmount}
                onChange={(e) => setEditAmount(parseInt(e.target.value) || 0)}
                fullWidth
                helperText="Enter the new amount for this entry"
                sx={{
                  mt: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />

              <TextField
                label="Paddle Subscription ID"
                value={editPaddleSubscriptionId}
                onChange={(e) => setEditPaddleSubscriptionId(e.target.value)}
                fullWidth
                helperText="Enter the Paddle subscription ID (e.g., sub_01abc123...)"
                sx={{
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              if (!editingEntry) return;

              setError(null);
              try {
                // Check if anything changed
                const amountChanged = editAmount !== editingEntry.value;
                const currentPaddleId = editingEntry.paddleSubscriptionId || editingEntry.subscription?.paddleSubscriptionId || "";
                const paddleIdChanged = editPaddleSubscriptionId !== currentPaddleId;

                if (!amountChanged && !paddleIdChanged) {
                  setEditDialogOpen(false);
                  return;
                }

                // Use the new update API
                const updates: {
                  value?: number;
                  paddleSubscriptionId?: string;
                } = {};

                if (amountChanged) {
                  updates.value = editAmount;
                }

                if (paddleIdChanged) {
                  updates.paddleSubscriptionId = editPaddleSubscriptionId || undefined;
                }

                await adminAPI.updateCreditLedgerEntry(editingEntry._id, updates);

                fetchCredits();
                setEditDialogOpen(false);
                setEditingEntry(null);
                setEditPaddleSubscriptionId("");
              } catch (err: any) {
                setError(err.response?.data?.error?.message || "Failed to edit credit entry");
                setEditDialogOpen(false);
              }
            }}
            color="primary"
            variant="contained"
            disabled={editAmount < 0}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove All Credits Dialog */}
      <Dialog
        open={removeAllDialogOpen}
        onClose={() => {
          setRemoveAllDialogOpen(false);
          setConfirmText("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="error" />
            <Typography variant="h6" color="error">
              Remove All Credits - Danger Zone
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>WARNING:</strong> This action will remove ALL credits from this user's account across all credit types. This cannot be undone!
          </Alert>
          
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              This will delete all <strong>{totalEntries}</strong> ledger entries,
              including both Added and Used records.
            </Typography>
            {balances && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Current balances (will become 0):
                </Typography>
                {Object.entries(balances).map(([type, balance]) => (
                  balance > 0 && (
                    <Typography key={type} variant="body2" sx={{ mt: 0.5 }}>
                      • {type.replace(/_/g, " ")}: <strong>{balance} credits</strong>
                    </Typography>
                  )
                ))}
              </Box>
            )}
          </Box>
          
          <Typography variant="body2" sx={{ mb: 2 }}>
            To confirm this action, please type <strong>REMOVE ALL</strong> in the field below:
          </Typography>
          
          <TextField
            fullWidth
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type REMOVE ALL to confirm"
            error={confirmText !== "" && confirmText !== "REMOVE ALL"}
            helperText={confirmText !== "" && confirmText !== "REMOVE ALL" ? "Text doesn't match" : ""}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontFamily: 'monospace',
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setRemoveAllDialogOpen(false);
              setConfirmText("");
            }}
            disabled={removingAll}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleRemoveAllCredits} 
            color="error" 
            variant="contained"
            disabled={confirmText !== "REMOVE ALL" || removingAll}
            startIcon={removingAll ? <CircularProgress size={20} /> : <DeleteForeverIcon />}
          >
            {removingAll ? "Removing..." : "Remove All Credits"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Expired Credits Dialog */}
      <Dialog
        open={removeExpiredDialogOpen}
        onClose={() => setRemoveExpiredDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            <Typography variant="h6">
              Remove Expired Credits
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will remove all expired credit entries from the user's ledger history.
          </Alert>
          
          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Expired ledger entries to be deleted: <strong>{expiredCreditsCount}</strong>
            </Typography>
            {credits.filter(credit => {
              if (!credit.expiresAt) return false;
              return new Date(credit.expiresAt) < new Date();
            }).slice(0, 10).map((credit) => (
              <Typography key={credit._id} variant="body2" sx={{ mt: 1 }}>
                • {credit.type === "credit" ? "+" : "-"}{Math.abs(credit.value)} {credit.creditType.replace(/_/g, " ")} 
                ({credit.type}) - expired {new Date(credit.expiresAt!).toLocaleDateString()}
              </Typography>
            ))}
            {expiredCreditsCount > 10 && (
              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                ...and {expiredCreditsCount - 10} more entries
              </Typography>
            )}
          </Box>
          
          <Typography variant="body2" color="text.secondary">
            <strong>Note:</strong> This action will remove the expired credit entries from the ledger history. 
            The credits have already expired and cannot be used.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setRemoveExpiredDialogOpen(false)}
            disabled={removingExpired}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleRemoveExpiredCredits} 
            color="warning" 
            variant="contained"
            disabled={removingExpired}
            startIcon={removingExpired ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {removingExpired ? "Removing..." : "Remove Expired Credits"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserCreditsTab;
